/**
 * The `/ignore` slash command - the chat trigger that arms the **ignore target
 * cursor** (the per-viewer squelch flow, ADR-0185). The pure, DOM-free classifier
 * lives here so it is node-testable in isolation, exactly like the `/duel`
 * classifier in {@link ./duel} it deliberately mirrors.
 *
 * Ignore used to be a row in the in-world long-press context menu on a sprite;
 * that row was removed (ADR-0185) and replaced by this command: the player types
 * `/ignore`, then clicks a player to squelch them - the same target-cursor pattern
 * as `/duel` (spoken phrase / alias arms a blue cursor, a click acts on the pick).
 * The two *other* ignore surfaces are unchanged - right-clicking a chat-journal
 * line ({@link ./ignoreMenu}) and the Settings ignore-list panel.
 *
 * Like `/duel`, `/ignore` is consumed **client-side** (it arms the cursor and is
 * never broadcast as chat), so the classifier reports both `arm` and `suppress`.
 * The actual add-to-list, self-guard, and name resolution live in `main.ts`; this
 * module is only the trigger match.
 */

/**
 * The `/ignore` slash command that arms the ignore target cursor (ADR-0185).
 * Normalized (trimmed + lower-cased) for the match so the trigger has one source
 * of truth the test pins - the {@link DUEL_COMMAND} pattern.
 */
export const IGNORE_COMMAND = '/ignore';

/**
 * Whether a line the player just typed is the `/ignore` command. Matched on the
 * exact command, trimmed + case-insensitively, so ordinary chat that merely
 * mentions it never traps the speaker into targeting mode - the mirror of
 * `matchesDuelCommand`.
 */
export function matchesIgnoreCommand(text: string): boolean {
  return text.trim().toLowerCase() === IGNORE_COMMAND;
}

/** How the chat-input router should treat a just-typed line for ignore purposes. */
export interface IgnoreChatIntent {
  /** Arm the ignore target cursor (`armIgnoreTargeting`). */
  arm: boolean;
  /**
   * The line is a client-side slash command and must **not** broadcast as speech
   * (like `/duel` / `/dress`); always true when `arm` is, since `/ignore` has no
   * spoken form.
   */
  suppress: boolean;
}

/**
 * Classify a just-typed line for the `/ignore` command (the twin of
 * `classifyDuelChat`): the exact command arms the ignore target cursor **and** is
 * suppressed (consumed client-side, never echoed as chat); any other line is inert.
 */
export function classifyIgnoreChat(text: string): IgnoreChatIntent {
  if (matchesIgnoreCommand(text)) return { arm: true, suppress: true };
  return { arm: false, suppress: false };
}
